import { ChevronRight } from "@mui/icons-material";
import cn from "classnames";
import Link from "next/link";
import { type FC } from "react";

import {
	type DocumentPageMetadata,
	type PageMetadata,
	type PublishSettings,
	makeUrlPrefix,
} from "@moment/api-collab/api-types";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/ui/Collapsible";
import {
	SidebarMenuAction,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
} from "~/ui/Sidebar";

import { type TreeItems } from "../../navigation-pane/types";

/**
 * This horrible component exists because `SidebarMenuSub` is a different thing than `SidebarMenu`,
 * and therefore must be the root of recursion. Ideally, we would recurse in `SidebarMenuItemTree`.
 */
const RecursiveSubMenu: FC<{
	workspaceName: string;
	documentId: string;
	activePage: DocumentPageMetadata | null;
	pageTreeChildren: TreeItems<PageMetadata>;
	urlPrefix: string;
}> = ({ workspaceName, documentId, activePage, pageTreeChildren, urlPrefix }) => {
	return pageTreeChildren?.length ? (
		<>
			<CollapsibleTrigger asChild>
				<SidebarMenuAction className="data-[state=open]:rotate-90">
					<ChevronRight />
					<span className="sr-only">Toggle</span>
				</SidebarMenuAction>
			</CollapsibleTrigger>
			<CollapsibleContent>
				<SidebarMenuSub>
					{pageTreeChildren?.map((subItem) => (
						<Collapsible key={subItem.id} asChild defaultOpen>
							<SidebarMenuSubItem key={subItem.data.title}>
								<SidebarMenuSubButton
									isActive={subItem.id === activePage?.id}
									asChild
									className={cn({ "font-bold ": subItem.id === activePage?.id })}
								>
									<Link href={`${urlPrefix}/${subItem.data.slug}`}>
										<span>{subItem.data.title}</span>
									</Link>
								</SidebarMenuSubButton>
								{subItem.children?.length ? (
									<RecursiveSubMenu
										workspaceName={workspaceName}
										documentId={documentId}
										activePage={activePage}
										pageTreeChildren={subItem.children}
										urlPrefix={urlPrefix}
									/>
								) : null}
							</SidebarMenuSubItem>
						</Collapsible>
					))}
				</SidebarMenuSub>
			</CollapsibleContent>
		</>
	) : null;
};

export const SidebarMenuItemTree: FC<{
	workspaceName: string;
	documentId: string;
	activePage: DocumentPageMetadata | null;
	pageTree: TreeItems<PageMetadata>;
	publishSettings: PublishSettings | undefined;
}> = ({ workspaceName, documentId, activePage, pageTree, publishSettings }) => {
	const urlPrefix = makeUrlPrefix(workspaceName, documentId, publishSettings);

	return (
		<div>
			{pageTree.map((page) => {
				const isIndex = page.data.slug === "index";
				const href = isIndex ? urlPrefix : `${urlPrefix}/${page.data.slug}`;

				// LAME BUT IMPORTANT: Cause the Next page router to behave well with custom
				// domains. If we are routing, say, `moment.dev/docs`, then the index page will be
				// `/docs`. If you use a `Link`, Next page router will try to route to a named route
				// called `/docs`. We don't want that, we want to route to the index page.
				const pageLink = isIndex ? (
					<a href={href}>{page.data.title}</a>
				) : (
					<Link href={href}>{page.data.title}</Link>
				);

				return (
					<Collapsible
						key={page.id}
						asChild
						defaultOpen
						//defaultOpen={item.isActive}
					>
						<SidebarMenuItem>
							<SidebarMenuButton
								isActive={page.id === activePage?.id}
								asChild
								className={cn({ "font-bold ": page.id === activePage?.id })}
							>
								{pageLink}
							</SidebarMenuButton>
							<RecursiveSubMenu
								workspaceName={workspaceName}
								documentId={documentId}
								activePage={activePage}
								pageTreeChildren={page.children}
								urlPrefix={urlPrefix}
							/>
						</SidebarMenuItem>
					</Collapsible>
				);
			})}
		</div>
	);
};
